/**
 * VideoNewsStrip — "VİDEO HABERLER" homepage block
 * -----------------------------------------------
 * Fetches the current province's video list (dedicated videos + news with a
 * video_url, courtesy of the backend union) and lays out up to 7 cards in a
 * single responsive row above the main slider.
 *
 * The row is a static grid (no sliding), as requested by the user.
 * Clicking a card:
 *   • source == "news"  → `/haber/<slug>` (news detail with embedded player)
 *   • otherwise         → `/videolar/<id>` (dedicated video page)
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";
import api from "@/lib/api";
import { useProvince } from "@/contexts/ProvinceContext";

const MAX_ITEMS = 7;

const cardLink = (v) =>
  v?.source === "news" && v?.news_slug
    ? `/haber/${v.news_slug}`
    : `/videolar/${v.id}`;

export default function VideoNewsStrip() {
  const { selected } = useProvince();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({
      limit: String(MAX_ITEMS),
      include_news: "true",
    });
    if (selected?.id) qs.set("province_id", selected.id);
    api
      .get(`/videos?${qs}`)
      .then((r) => setItems((r.data.items || []).slice(0, MAX_ITEMS)))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [selected?.id]);

  if (!loading && items.length === 0) return null;

  return (
    <section data-testid="video-news-strip" className="mt-1">
      {/* Thin label strip */}
      <div className="flex items-center gap-2 mb-1.5">
        <span
          className="inline-block w-1 h-4 bg-[#e11d2a] rounded-sm"
          aria-hidden="true"
        ></span>
        <h3 className="text-[11px] font-bold tracking-widest text-gray-700 uppercase">
          Video Haberler
        </h3>
        <span className="flex-1 h-px bg-gray-200" aria-hidden="true"></span>
        <Link
          to="/videolar"
          className="text-[11px] font-semibold text-[#e11d2a] hover:underline"
          data-testid="video-news-strip-more"
        >
          Tümü →
        </Link>
      </div>

      {/* 7-up static grid (shrinks gracefully on small screens) */}
      <div
        className="grid gap-1.5"
        style={{ gridTemplateColumns: `repeat(${MAX_ITEMS}, minmax(0, 1fr))` }}
        data-testid="video-news-strip-grid"
      >
        {(loading ? Array.from({ length: MAX_ITEMS }) : items).map((v, i) => {
          if (!v) {
            return (
              <div
                key={`sk-${i}`}
                className="aspect-video rounded-md bg-gray-100 animate-pulse"
              />
            );
          }
          const title = v.title || "";
          return (
            <Link
              key={v.id}
              to={cardLink(v)}
              className="group relative block aspect-video rounded-md overflow-hidden bg-black ring-1 ring-black/5 hover:ring-[#e11d2a]/60 transition-all"
              data-testid={`video-news-card-${v.id}`}
              title={title}
            >
              {v.thumbnail ? (
                <img
                  src={v.thumbnail}
                  alt={title}
                  className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-300"
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-900" />
              )}
              {/* Dark gradient for title legibility */}
              <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 to-transparent pointer-events-none"></div>
              {/* Play button */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/85 group-hover:bg-[#e11d2a] group-hover:text-white text-black transition-colors shadow">
                  <Play className="w-3.5 h-3.5" fill="currentColor" />
                </span>
              </div>
              {/* Title */}
              <div className="absolute inset-x-0 bottom-0 px-1.5 pb-1 text-white text-[10px] md:text-[11px] font-semibold leading-tight line-clamp-2">
                {title}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
